import {useEffect, useId, useMemo, useRef, useState} from "react";
import AnimateHeight from "react-animate-height";
import ChevronIcon from "@autocx/icons/src/chevron-icon";
import {
    GROUP_FILTER_KEY,
    getFilterGroups,
    getGroupSelection,
    resolveGroupFilters,
    toggleGroupSelection,
} from "./filter-groups";

const asGroupIds = (value) => (Array.isArray(value) ? value : [value])
    .filter((id) => typeof id === "string" && id !== "");

const sameId = (left, right) => String(left).toLowerCase() === String(right).toLowerCase();

const setGroupIds = (activeFilters, ids) => {
    const uniqueIds = ids.filter((id, index) => ids.findIndex((candidate) => sameId(candidate, id)) === index);
    const next = {...activeFilters};
    if (!uniqueIds.length) delete next[GROUP_FILTER_KEY];
    else next[GROUP_FILTER_KEY] = uniqueIds.length === 1 ? uniqueIds[0] : uniqueIds;
    return next;
};

const toggleOption = (fieldId, value, activeFilters, storeSettings, availableValues) => {
    const active = activeFilters && typeof activeFilters === "object" && !Array.isArray(activeFilters)
        ? activeFilters
        : {};
    const ids = asGroupIds(active[GROUP_FILTER_KEY]);
    const currentGroups = getFilterGroups(storeSettings);
    const affectedGroup = currentGroups.find((group) => group.fieldId === fieldId && group.values.includes(value));
    const groupIsSelected = affectedGroup && ids.some((id) => sameId(id, affectedGroup.id));
    const idsForOtherGroups = affectedGroup
        ? ids.filter((id) => !sameId(id, affectedGroup.id))
        : ids;
    const currentValue = active[fieldId];
    const selectedValues = Array.isArray(currentValue)
        ? currentValue.filter((current, index) => currentValue.indexOf(current) === index)
        : currentValue === undefined || currentValue === null || currentValue === "" ? [] : [currentValue];

    // Expanding a selected group for a child change adds only choices the
    // shopper can currently see. Future or zero-hit members are never copied
    // into the raw field values implicitly.
    if (groupIsSelected) {
        affectedGroup.values.forEach((member) => {
            if (availableValues?.has(member) && !selectedValues.includes(member)) selectedValues.push(member);
        });
    }

    const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((current) => current !== value)
        : [...selectedValues, value];

    let next = {...active};
    delete next[fieldId];
    if (nextValues.length) next[fieldId] = nextValues;
    else delete next[fieldId];
    next = setGroupIds(next, idsForOtherGroups);

    // Store a complete set of visible children as the stable group ID. This
    // keeps future members attached to the group in saved URLs.
    const visibleMembers = affectedGroup?.values.filter((member) => availableValues?.has(member)) || [];
    if (visibleMembers.length && visibleMembers.every((member) => nextValues.includes(member))) {
        const remainingValues = nextValues.filter((member) => !visibleMembers.includes(member));
        if (remainingValues.length) next[fieldId] = remainingValues;
        else delete next[fieldId];
        next = setGroupIds(next, [...idsForOtherGroups, affectedGroup.id]);
    }

    return next;
};

const getCurrentGroups = (groups, storeSettings) => {
    const configuredGroups = getFilterGroups(storeSettings);
    return (Array.isArray(groups) ? groups : []).reduce((result, serverGroup) => {
        const configured = configuredGroups.find((candidate) => sameId(candidate.id, serverGroup?.id));
        if (!configured) return result;
        result.push({
            ...configured,
            ...(serverGroup?.count !== undefined && serverGroup?.count !== null && Number.isFinite(+serverGroup.count)
                ? {count: +serverGroup.count}
                : {}),
        });
        return result;
    }, []);
};

const getOptionLabel = (option, value) => String(option?.label ?? option?.value ?? value ?? "");

function FilterGroupOptions({
    fieldId,
    group,
    groupOptions,
    selection,
    selectedValues,
    allowExpansion,
    activeFilters,
    storeSettings,
    onChange,
    groupKey,
    availableValues,
}) {
    const [isExpanded, setExpanded] = useState(false);
    const checkboxRef = useRef(null);
    const optionsId = `${groupKey}-options`;
    const groupAllowsExpansion = group.allowExpansion ?? allowExpansion;
    const selected = selection.checked || selection.selectedCount > 0;
    const disabled = group.count === 0 && !selected;

    useEffect(() => {
        if (checkboxRef.current) checkboxRef.current.indeterminate = selection.indeterminate;
    }, [selection.indeterminate]);

    return (
        <div>
            <div className="flex items-center justify-between gap-2">
                <div className="relative flex min-w-0 items-start">
                    <div className="flex h-5 items-center">
                        <input
                            ref={checkboxRef}
                            id={`${groupKey}-checkbox`}
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 shadow-sm focus:ring-primary-500"
                            checked={selection.checked}
                            aria-checked={selection.indeterminate ? "mixed" : selection.checked}
                            disabled={disabled}
                            onChange={() => onChange(toggleGroupSelection(group,
                                selection.checked ? setGroupIds(activeFilters, [...asGroupIds(activeFilters[GROUP_FILTER_KEY]), group.id]) : activeFilters,
                                storeSettings))}
                        />
                    </div>
                    <div className="ml-2 min-w-0 text-sm">
                        <label htmlFor={`${groupKey}-checkbox`} className="font-medium">
                            {group.label}
                        </label>
                        {Number.isFinite(group.count) && (
                            <span className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                                {group.count}
                            </span>
                        )}
                    </div>
                </div>
                {groupAllowsExpansion && (
                    <button
                        type="button"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-600 hover:text-link focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        aria-expanded={isExpanded}
                        aria-controls={optionsId}
                        aria-label={`${isExpanded ? "Hide" : "Show"} ${group.label} options`}
                        onClick={() => setExpanded((expanded) => !expanded)}
                    >
                        <span className={`border-current ${isExpanded ? "chevron--up" : "chevron--down"}`}>
                            <ChevronIcon className="h-5 w-5" />
                        </span>
                    </button>
                )}
            </div>
            {groupAllowsExpansion && !isExpanded && selection.indeterminate && (
                <p className="ml-6 mt-1 text-xs text-gray-500">
                    {selection.selectedCount} of {groupOptions.length} selected
                </p>
            )}
            <AnimateHeight
                id={optionsId}
                height={groupAllowsExpansion && isExpanded ? "auto" : 0}
                animateOpacity={true}
                className="ml-6"
                contentClassName="pt-1 space-y-2"
                role="group"
                aria-label={`${group.label} options`}
            >
                {groupOptions.map((option, index) => {
                    const isSelected = selectedValues.has(option.value);
                    const optionId = `${groupKey}-option-${index}`;
                    return (
                        <div key={option.value} className="relative flex items-start">
                            <div className="flex h-5 items-center">
                                <input
                                    id={optionId}
                                    type="checkbox"
                                    name={fieldId}
                                    value={option.value}
                                    className="h-4 w-4 rounded border-gray-300 shadow-sm focus:ring-primary-500"
                                    checked={isSelected}
                                    disabled={option.count === 0 && !isSelected}
                                    onChange={() => onChange(toggleOption(fieldId, option.value, activeFilters, storeSettings, availableValues))}
                                />
                            </div>
                            <div className="ml-2 min-w-0 text-sm">
                                <label htmlFor={optionId} className="font-medium">
                                    {option.label}
                                </label>
                                <span className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                                    {option.count}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </AnimateHeight>
        </div>
    );
}

export default function GroupedFilterOptions({
    fieldId,
    label,
    options = [],
    groups = [],
    allowExpansion = false,
    activeFilters = {},
    storeSettings,
    onChange = () => {},
}) {
    const reactId = useId();
    const currentGroups = useMemo(() => getCurrentGroups(groups, storeSettings), [groups, storeSettings]);
    const allOptions = Array.isArray(options) ? options : [];
    const optionsByValue = new Map(allOptions
        .filter((option) => typeof option?.value === "string")
        .map((option) => [option.value, option]));
    const groupedValues = new Set(currentGroups.flatMap((group) => group.values));
    const ungroupedOptions = allOptions.filter((option) => (
        typeof option?.value !== "string" || !groupedValues.has(option.value)
    ));
    const resolvedFilters = resolveGroupFilters(activeFilters, storeSettings);
    const currentFieldValue = resolvedFilters[fieldId];
    const selectedValues = new Set(Array.isArray(currentFieldValue)
        ? currentFieldValue
        : currentFieldValue === undefined || currentFieldValue === null || currentFieldValue === ""
            ? []
            : [currentFieldValue]);

    const availableValues = new Set(allOptions.filter(option => option?.count > 0).map(option => option.value));
    const changeOption = (value) => onChange(toggleOption(fieldId, value, activeFilters, storeSettings, availableValues));

    return (
        <div className="space-y-3" aria-label={label}>
            {currentGroups.filter(group => group.count === undefined || group.count > 0).map((group) => {
                const fullSelection = getGroupSelection(group, activeFilters, storeSettings);
                const groupOptions = group.values.map((value) => {
                    const option = optionsByValue.get(value);
                    return {
                        ...(option || {}),
                        value,
                        label: getOptionLabel(option, value),
                        count: option?.count !== undefined && option?.count !== null && Number.isFinite(+option.count)
                            ? +option.count
                            : 0,
                    };
                });
                // Future members stay in configuration and URL resolution, but only
                // values with matching products belong in the shopper's choices.
                const visibleGroupOptions = groupOptions.filter(option => option.count > 0);
                const selectedCount = visibleGroupOptions.filter(option => selectedValues.has(option.value)).length;
                const checked = fullSelection.checked || (visibleGroupOptions.length > 0 && selectedCount === visibleGroupOptions.length);
                const selection = {checked, selectedCount, indeterminate: !checked && selectedCount > 0};
                const groupKey = `${reactId}-${group.id}`;

                return (
                    <FilterGroupOptions
                        key={groupKey}
                        fieldId={fieldId}
                        group={group}
                        groupOptions={visibleGroupOptions}
                        selection={selection}
                        selectedValues={selectedValues}
                        allowExpansion={allowExpansion}
                        activeFilters={activeFilters}
                        storeSettings={storeSettings}
                        onChange={onChange}
                        groupKey={groupKey}
                        availableValues={availableValues}
                    />
                );
            })}

            {ungroupedOptions.map((option, index) => {
                const value = option?.value;
                const checked = selectedValues.has(value);
                const id = `${reactId}-${fieldId}-ungrouped-${index}`;
                return (
                    <div key={`${String(value)}-${index}`} className="relative flex items-start">
                        <div className="flex h-5 items-center">
                            <input
                                id={id}
                                type="checkbox"
                                name={fieldId}
                                value={value}
                                className="h-4 w-4 rounded border-gray-300 shadow-sm focus:ring-primary-500"
                                checked={checked}
                                disabled={option?.count === 0 && !checked}
                                onChange={() => changeOption(value)}
                            />
                        </div>
                        <div className="ml-2 min-w-0 text-sm">
                            <label htmlFor={id} className="font-medium">
                                {getOptionLabel(option, value)}
                            </label>
                            {option?.count !== undefined && option?.count !== null && (
                                <span className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                                    {option.count}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
